import { AGE_TYPE_CONST } from '@/constants/type';
import {
  UNDER_24_MONTHS_AGE_IDS,
  UNDER_24_MONTHS_AGE_IDS_EXCLUDE_DT00,
  FROM_24_TO_60_MONTHS_AGE_IDS,
  FROM_60_TO_72_MONTHS_AGE_IDS,
  MEALS_PER_DAY_OPTIONS,
  ratioSignOptions,
  AGE_IDS_HAS_AFTERNOON_MEAL
} from '@/utils/nutrition-columns.utils';

export function getEditableCellProps({
  keyName,
  record,
  editedRows,
  listAge,
  options
}: {
  keyName: string;
  record: any;
  editedRows: Record<string, any>;
  listAge?: any[];
  options?: { value: string | number; label: string }[];
}) {
  const value = editedRows[record.id]?.[keyName] ?? record[keyName];
  const ageId = editedRows[record.id]?.ageRangeId ?? record.ageRangeId;
  let found = null;
  if (listAge) {
    found = listAge.find((item: any) => item.id === ageId);
  }

  switch (keyName) {
    case 'genderId':
      return {
        type: 'select',
        value,
        options: options || [],
        required: true
      };
    case 'breastMilkLevel': {
      const isUnder24MonthsExcludeDT00 =
        found && UNDER_24_MONTHS_AGE_IDS_EXCLUDE_DT00.includes(found.id);
      return {
        type: 'select',
        value,
        options: options || [],
        required: isUnder24MonthsExcludeDT00,
        disabled: !isUnder24MonthsExcludeDT00
      };
    }
    case 'standardMealsPerDay': {
      return {
        type: 'select',
        value,
        options: MEALS_PER_DAY_OPTIONS,
        required: true
      };
    }
    case 'healthStatusId':
    case 'healthStatusIds': {
      let isUnder19 = false;
      if (found) {
        isUnder19 = found.healthStatusBmiType !== AGE_TYPE_CONST.BMI_ADULT;
      }
      const normalizedValue = Array.isArray(value)
        ? value
        : value
          ? [value]
          : [];
      return {
        type: 'multi-select',
        value: normalizedValue,
        options: options || [],
        isUnder19,
        required: true
      };
    }
    case 'complementaryFoodEnergyMin':
    case 'complementaryFoodEnergyMax':
    case 'breastMilkEnergy': {
      const isUnder24Months =
        found && UNDER_24_MONTHS_AGE_IDS.includes(found.id);
      return {
        type: 'decimal-input',
        value,
        required: isUnder24Months,
        disabled: !isUnder24Months
      };
    }
    case 'standardPlgDayPpercentMin':
    case 'standardPlgDayPpercentMax':
    case 'standardPlgDayLpercentMin':
    case 'standardPlgDayLpercentMax':
    case 'standardPlgDayGpercentMin':
    case 'standardPlgDayGpercentMax':
      return {
        type: 'integer-input',
        value,
        min: 0,
        max: 100,
        required: true
      };
    case 'standardPlgDayPgramMin':
    case 'standardPlgDayPgramMax':
    case 'standardPlgDayLgramMin':
    case 'standardPlgDayLgramMax':
    case 'standardPlgDayGgramMin':
    case 'standardPlgDayGgramMax':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: true
      };
    case 'standardPlgDayPRatio':
      return {
        type: 'custom-input',
        value,
        required: false, // Not required anymore, using pProportionOperator + pProportionValue instead
        validate: validatePRatioFormat
      };
    case 'weightMin':
    case 'weightMax':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 500,
        required: true
      };
    case 'metabolismBasic':
    case 'energyConsumption':
    case 'energyNeeded':
    case 'energyMin':
    case 'energyMax':
    case 'lProportionValue':
      return {
        type: 'decimal-input',
        value,
        required: true
      };
    case 'milkUnitsPerDay': {
      const isUnder24 = found && UNDER_24_MONTHS_AGE_IDS.includes(found.id);
      const is24To60 = found && FROM_24_TO_60_MONTHS_AGE_IDS.includes(found.id);
      const is60To72 = found && FROM_60_TO_72_MONTHS_AGE_IDS.includes(found.id);
      return {
        type: 'decimal-input',
        value,
        required: !!is24To60 || !!is60To72,
        disabled: !!isUnder24
      };
    }
    case 'lProportionOperator':
    case 'pProportionOperator': {
      // Only required if pProportionValue is provided
      const pValue =
        editedRows[record.id]?.pProportionValue ?? record.pProportionValue;
      return {
        type: 'select',
        value,
        options: ratioSignOptions,
        required: pValue !== null && pValue !== undefined && pValue !== ''
      };
    }
    case 'pProportionValue': {
      // Only required if pProportionOperator is provided
      const pOperator =
        editedRows[record.id]?.pProportionOperator ??
        record.pProportionOperator;
      return {
        type: 'decimal-input',
        value,
        required:
          pOperator !== null && pOperator !== undefined && pOperator !== ''
      };
    }
    case 'standardEnergyDistributionBsmin':
    case 'standardEnergyDistributionBsmax': {
      const isRequired = !(found && found.id === 'DT01');
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 100,
        required: isRequired
      };
    }
    case 'standardEnergyDistributionBtrmin':
    case 'standardEnergyDistributionBtrmax':
    case 'standardEnergyDistributionBtmin':
    case 'standardEnergyDistributionBtmax':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 100,
        required: true
      };
    case 'standardEnergyDistributionBP_1min':
    case 'standardEnergyDistributionBP_1max':
    case 'standardEnergyDistributionBP_2min':
    case 'standardEnergyDistributionBP_2max':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 100,
        required: false
      };

    case 'standardEnergyDistributionBcmin':
    case 'standardEnergyDistributionBcmax': {
      const isRequired = found && AGE_IDS_HAS_AFTERNOON_MEAL.includes(found.id);
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 100,
        required: isRequired
      };
    }

    case 'standardPlgBreakfastPpercentMin':
    case 'standardPlgBreakfastPpercentMax':
    case 'standardPlgBreakfastLpercentMin':
    case 'standardPlgBreakfastLpercentMax':
    case 'standardPlgBreakfastGpercentMin':
    case 'standardPlgBreakfastGpercentMax':
      return {
        type: 'integer-input',
        value,
        min: 0,
        max: 100,
        required: false
      };

    case 'standardPlgBreakfastPgramMin':
    case 'standardPlgBreakfastPgramMax':
    case 'standardPlgBreakfastLgramMin':
    case 'standardPlgBreakfastLgramMax':
    case 'standardPlgBreakfastGgramMin':
    case 'standardPlgBreakfastGgramMax':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: false
      };

    case 'standardPlgLunchPpercentMin':
    case 'standardPlgLunchPpercentMax':
    case 'standardPlgLunchLpercentMin':
    case 'standardPlgLunchLpercentMax':
    case 'standardPlgLunchGpercentMin':
    case 'standardPlgLunchGpercentMax':
      return {
        type: 'integer-input',
        value,
        min: 0,
        max: 100,
        required: true
      };

    case 'standardPlgLunchPgramMin':
    case 'standardPlgLunchPgramMax':
    case 'standardPlgLunchLgramMin':
    case 'standardPlgLunchLgramMax':
    case 'standardPlgLunchGgramMin':
    case 'standardPlgLunchGgramMax':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: true
      };

    case 'standardPlgAfternoonPpercentMin':
    case 'standardPlgAfternoonPpercentMax':
    case 'standardPlgAfternoonLpercentMin':
    case 'standardPlgAfternoonLpercentMax':
    case 'standardPlgAfternoonGpercentMin':
    case 'standardPlgAfternoonGpercentMax':
      return {
        type: 'integer-input',
        value,
        min: 0,
        max: 100,
        required: false
      };

    case 'standardPlgAfternoonPgramMin':
    case 'standardPlgAfternoonPgramMax':
    case 'standardPlgAfternoonLgramMin':
    case 'standardPlgAfternoonLgramMax':
    case 'standardPlgAfternoonGgramMin':
    case 'standardPlgAfternoonGgramMax':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: false
      };

    case 'standardPlgDinnerPpercentMin':
    case 'standardPlgDinnerPpercentMax':
    case 'standardPlgDinnerLpercentMin':
    case 'standardPlgDinnerLpercentMax':
    case 'standardPlgDinnerGpercentMin':
    case 'standardPlgDinnerGpercentMax':
      return {
        type: 'integer-input',
        value,
        min: 0,
        max: 100,
        required: true
      };

    case 'standardPlgDinnerPgramMin':
    case 'standardPlgDinnerPgramMax':
    case 'standardPlgDinnerLgramMin':
    case 'standardPlgDinnerLgramMax':
    case 'standardPlgDinnerGgramMin':
    case 'standardPlgDinnerGgramMax':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: true
      };

    case 'standardPlgExtra1PpercentMin':
      return {
        type: 'integer-input',
        value,
        min: 0,
        max: 100,
        required: false
      };
    case 'standardPlgExtra1PpercentMax':
    case 'standardPlgExtra1LpercentMin':
    case 'standardPlgExtra1LpercentMax':
    case 'standardPlgExtra1GpercentMin':
    case 'standardPlgExtra1GpercentMax':
      return {
        type: 'integer-input',
        value,
        min: 0,
        max: 100,
        required: false
      };

    case 'standardPlgExtra1PgramMin':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: false
      };
    case 'standardPlgExtra1PgramMax':
    case 'standardPlgExtra1LgramMin':
    case 'standardPlgExtra1LgramMax':
    case 'standardPlgExtra1GgramMin':
    case 'standardPlgExtra1GgramMax':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: false
      };

    case 'standardFruitDay':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: true
      };
    case 'standardFruitBs':
    case 'standardFruitBc':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: false
      };
    case 'standardFruitBtr':
    case 'standardFruitBt':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: true
      };
    case 'standardVegetableDay':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: true
      };
    case 'standardVegetableBs':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: false
      };
    case 'standardVegetableBtr':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: true
      };
    case 'standardVegetableBc':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: false
      };
    case 'standardVegetableBt':
      return {
        type: 'decimal-input',
        value,
        min: 0,
        max: 10000,
        required: true
      };

    case 'standardSaltDay':
      return {
        type: 'custom-input',
        value,
        required: true,
        validate: validateSaltInputFormat,
        placeholder:
          '[number], <[number], >[number], <=[number], >=[number], [number]-[number]'
      };
    case 'standardSaltBt':
    case 'standardSaltBs':
    case 'standardSaltBtr':
    case 'standardSaltBc':
    case 'standardSaltBp':
      return {
        type: 'custom-input',
        value,
        required: false,
        validate: validateSaltInputFormat,
        placeholder:
          '[number], <[number], >[number], <=[number], >=[number], [number]-[number]'
      };
    case 'standardSaltStruct':
      return {
        type: 'readonly',
        value,
        readOnly: true
      };
    case 'calciStandardDay':
    case 'waterStandardDayMin':
    case 'waterStandardDayMax':
    case 'fiberStandardDay':
    case 'kaliStandardDay':
    case 'cholesterolStandardDay':
    case 'natriStandardDay':
    case 'cloStandardDay':
    case 'phosphoStandardDay':
    case 'magieStandardDay':
    case 'ironStandardDay':
    case 'zincStandardDay':
    case 'iodStandardDay':
    case 'selenStandardDay':
    case 'copperStandardDay':
    case 'cromStandardDay':
    case 'manganStandardDay':
    case 'fluorStandardDay':
    case 'vitaminAStandardDay':
    case 'vitaminDStandardDay':
    case 'vitaminEStandardDay':
    case 'vitaminKStandardDay':
    case 'vitaminB1StandardDay':
    case 'vitaminB2StandardDay':
    case 'vitaminB3StandardDay':
    case 'vitaminB5StandardDay':
    case 'vitaminB6StandardDay':
    case 'vitaminB9StandardDay':
    case 'vitaminB12StandardDay':
    case 'biotinStandardDay':
    case 'cholineStandardDay':
    case 'vitaminCStandardDay':
    case 'totalUnsaturated':
      return {
        type: 'string-input',
        value,
        required: false
      };
    case 'totalDay':
    case 'totalLunch':
    case 'totalDinner':
      return {
        type: 'integer-input',
        value,
        min: 0,
        max: 10000,
        required: true
      };
    case 'sugarStandardDay':
      return {
        type: 'custom-input',
        value,
        required: false,
        validate: validateComparisonFormat
      };
    case 'backup1StandardDayMin':
    case 'backup1StandardDayMax':
    case 'backup2StandardDayMin':
    case 'backup2StandardDayMax':
    case 'backup3StandardDayMin':
    case 'backup3StandardDayMax':
    case 'backup4StandardDayMin':
    case 'backup4StandardDayMax':
    case 'backup5StandardDayMin':
    case 'backup5StandardDayMax':
    case 'backup6StandardDayMin':
    case 'backup6StandardDayMax':
    case 'backup7StandardDayMin':
    case 'backup7StandardDayMax':
    case 'backup8StandardDayMin':
    case 'backup8StandardDayMax':
    case 'backup9StandardDayMin':
    case 'backup9StandardDayMax':
    case 'backup10StandardDayMin':
    case 'backup10StandardDayMax':
      return {
        type: 'custom-input',
        value,
        required: false
      };
    case 'nutritionStatusZscore': {
      const isZscore =
        found && found.healthStatusBmiType === AGE_TYPE_CONST.ZSCORE;
      return {
        type: 'select',
        value,
        options: options || [],
        required: isZscore,
        disabled: !isZscore
      };
    }
    case 'nutritionStatusBmiChild': {
      const isBmiChild =
        found && found.healthStatusBmiType === AGE_TYPE_CONST.BMI_CHILD;
      return {
        type: 'select',
        value,
        options: options || [],
        required: isBmiChild,
        disabled: !isBmiChild
      };
    }
    case 'nutritionStatusBmiAdult': {
      const isBmiAdult =
        found && found.healthStatusBmiType === AGE_TYPE_CONST.BMI_ADULT;
      return {
        type: 'select',
        value,
        options: options || [],
        required: isBmiAdult,
        disabled: !isBmiAdult
      };
    }
    case 'physicalActivityId':
      return {
        type: 'select',
        value,
        options: options || [],
        required: true
      };
    default:
      return {
        type: 'default',
        value,
        required: keyName === 'ageRangeId' ? true : false
      };
  }
}

function validateSaltInputFormat(value: string) {
  if (!value) return true;
  // [number]
  if (/^\d+(\.\d+)?$/.test(value)) return true;
  // <[number], >[number], <=[number], >=[number]
  if (/^(<|>|<=|>=)\d+(\.\d+)?$/.test(value)) return true;
  // [number]-[number]
  if (/^\d+(\.\d+)?-\d+(\.\d+)?$/.test(value)) return true;
  return false;
}

function validateComparisonFormat(value: string) {
  if (!value) return true;
  // Chỉ số, ví dụ: 90, 12.5 hoặc 12,5
  if (/^\d+([.,]\d+)?$/.test(value)) return true;
  // Dấu < + số, ví dụ: <90, <12.5 hoặc <12,5
  if (/^<\d+([.,]\d+)?$/.test(value)) return true;

  return false;
}
function validatePRatioFormat(value: string) {
  if (!value) return true;
  // [number] (ví dụ: 90, 12.5 hoặc 12,5)
  if (/^\d+([.,]\d+)?$/.test(value)) return true;
  // <[number], >[number], <=[number], >=[number], =[number]
  // Ví dụ: <90, >=12.5 hoặc >=12,5
  if (/^(<|>|<=|>=|=)\d+([.,]\d+)?$/.test(value)) return true;
  return false;
}
